import { useState, useRef, useEffect } from "react";
import { nexusApi } from "../api";

// Accept businessId and facilityId as props
interface NexusProps {
  businessId: string;
  facilityId: string;
}

const Nexus: React.FC<NexusProps> = ({ businessId, facilityId }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<{ role: string; content: string }[]>([]);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open]);

  const sendMessage = async () => {
    if (!input.trim()) return;

    // Use the props directly
    if (!businessId || !facilityId) {
      setMessages((msgs) => [
        ...msgs,
        { role: "assistant", content: "Please select a facility before using the agent." },
      ]);
      return;
    }

    const userMessage = { role: "user", content: input };
    setMessages((msgs) => [...msgs, userMessage]);
    setInput("");

    try {
      const response = await nexusApi.sendMessage(input, businessId, facilityId);
      setMessages((msgs) => [
        ...msgs,
        { role: "assistant", content: response || "Sorry, I didn't get that." },
      ]);
    } catch (err) {
      setMessages((msgs) => [
        ...msgs,
        { role: "assistant", content: "Error contacting server." },
      ]);
    }
  };

  return (
    <div>
      {/* Floating Button */}
      <button
        className="fixed bottom-8 right-8 z-50 bg-indigo-600 text-white rounded-full p-4 shadow-lg hover:bg-indigo-700 transition"
        onClick={() => setOpen((o) => !o)}
        aria-label="Open chatbot"
      >
        💬
      </button>

      {/* Chat Window */}
      {open && (
        <div className="fixed bottom-24 right-8 z-50 w-80 max-w-full bg-white rounded-xl shadow-2xl flex flex-col border border-gray-200 min-h-64" style={{ minHeight: 400 }}>
          <div className="flex items-center justify-between p-4 border-b">
            <span className="font-bold text-indigo-700">Nexus - Courted.ai's LLM Agent</span>
            <button
              className="text-gray-400 hover:text-gray-700"
              onClick={() => setOpen(false)}
              aria-label="Close chatbot"
            >
              ✕
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-2" style={{ maxHeight: 350 }}>
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`px-3 py-2 rounded-lg text-sm ${
                    msg.role === "user"
                      ? "bg-indigo-100 text-indigo-900"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <form
            className="flex border-t p-2"
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage();
            }}
          >
            <input
              className="flex-1 px-3 py-2 rounded-l-lg border border-gray-200 focus:outline-none"
              type="text"
              placeholder="Type your message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
            />
            <button
              type="submit"
              className="bg-indigo-600 text-white px-4 py-2 rounded-r-lg hover:bg-indigo-700 transition"
            >
              Send
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Nexus;
